import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
    AccountEntity,
    AccountsResponse,
    CommentEntity,
    CommentsResponse,
    GroupEntity,
    GroupsResponse,
    ModActionLogEntity,
    PhotoEntity,
    PhotosResponse,
    PostEntity,
    PostsResponse,
    UserReportsEntity,
} from '@interpals/api-web';

import { ModContentStatus } from 'pages/Moderation/ModRecentContentPage/types';
import api from 'api';
import { ReduxState } from 'redux/root-reducer';
import { MODERATION_RECENT_CONTENT_LIMIT } from 'constants/limits';
import { selectLogItemsByUid, selectUserReports } from 'redux/moderation/selectors';

import { ModReportFilters } from './reducer';

export const REPORTS_LIMIT = 25;

export const getAllReports = createAsyncThunk<UserReportsEntity[], number, { state: ReduxState }>(
    'get/all-reports',
    async (page, { getState, dispatch }) => {
        const { filters } = getState().moderation;
        const reason_category = filters.reason_category === 'all reasons' ? undefined : filters.reason_category;
        const entity_type = filters.entity_type === 'all content types' ? undefined : filters.entity_type;
        const offsetByPage = (page - 1) * REPORTS_LIMIT;

        dispatch(resetReportsList());

        return await api
            .getUserReportsGroupedByUser({
                limit: REPORTS_LIMIT,
                offset: offsetByPage,
                reason_category,
                entity_type,
                status: filters.report_status,
                sort: filters.sort,
                extra: 'account.ips,comment.photo,comment.photo.user',
            })
            .then((response) => {
                dispatch(setReportsTotal(response.total));

                return response.data;
            });
    },
);

export const getUserReports = createAsyncThunk<UserReportsEntity, string, { state: ReduxState }>(
    'get/user-reports',
    async (uid, { getState }) => {
        const filter = getState().moderation.filters;
        const reason_category = filter.reason_category === 'all reasons' ? undefined : filter.reason_category;
        const entity_type = filter.entity_type === 'all content types' ? undefined : filter.entity_type;

        const reports = await api
            .getUserReportsGroupedByUser({
                uid,
                status: filter.report_status,
                reason_category,
                entity_type,
                extra: 'account.ips,comment.photo,comment.photo.user',
            })
            .then((response) => response.data);

        if (reports.length > 0) {
            return reports[0];
        }

        // There are no reports for this user, but we still need to return the user's
        // account entity so that we can display the user's profile information
        // (this is for the moderate user route)
        let account = selectUserReports(getState(), uid)?.account;
        if (!account) {
            account = await api.getAccount({ id: uid, extra: 'ips' });
        }

        return { account, reports: [] };
    },
);

export const getModRecentContentUsers = createAsyncThunk<AccountsResponse, ModContentStatus[], { state: ReduxState }>(
    'get/mod-recent-content-users',
    async (status, { getState }) => {
        const offset = getState().moderation.recentContent.content.users.length;

        return await api.getAccountsByModerationStatus({
            mod_status: status,
            limit: MODERATION_RECENT_CONTENT_LIMIT,
            offset: offset,
            extra: 'ips',
        });
    },
);

export const getModRecentContentGroups = createAsyncThunk<GroupsResponse, ModContentStatus[], { state: ReduxState }>(
    'get/mod-recent-content-groups',
    async (status, { getState }) => {
        const offset = getState().moderation.recentContent.content.groups.length;

        return await api.getGroupsByModerationStatus({
            mod_status: status,
            limit: MODERATION_RECENT_CONTENT_LIMIT,
            offset: offset,
        });
    },
);

export const getModRecentContentComments = createAsyncThunk<
    CommentsResponse,
    ModContentStatus[],
    { state: ReduxState }
>('get/mod-recent-content-comments', async (status, { getState }) => {
    const offset = getState().moderation.recentContent.content.comments.length;

    return await api.getCommentsByModerationStatus({
        mod_status: status,
        limit: MODERATION_RECENT_CONTENT_LIMIT,
        offset: offset,
        extra: 'photo,photo.user',
    });
});

export const getModRecentContentPhotos = createAsyncThunk<PhotosResponse, ModContentStatus[], { state: ReduxState }>(
    'get/mod-recent-content-photos',
    async (status, { getState }) => {
        const offset = getState().moderation.recentContent.content.photos.length;

        return await api.getPhotosByModerationStatus({
            mod_status: status,
            limit: MODERATION_RECENT_CONTENT_LIMIT,
            offset: offset,
        });
    },
);

export const getModRecentContentPosts = createAsyncThunk<PostsResponse, ModContentStatus[], { state: ReduxState }>(
    'get/mod-recent-content-posts',
    async (status, { getState }) => {
        const offset = getState().moderation.recentContent.content.posts.length;

        return await api.getPostsByModerationStatus({
            mod_status: status,
            limit: MODERATION_RECENT_CONTENT_LIMIT,
            offset: offset,
        });
    },
);

export const MOD_LOG_LIMIT_PER_FETCH = 5;

interface GetModLogItemsByUidParams {
    userId: string;
    // Use null to fetch the first page (overwriting the existing log items)
    offsetLogId?: string | null;
}

export const getModLogItemsByUid = createAsyncThunk<
    ModActionLogEntity[],
    GetModLogItemsByUidParams,
    {
        state: ReduxState;
    }
>('get/mod-log-items-by-uid', async ({ userId, offsetLogId }, { getState }) => {
    let effectiveOffset = offsetLogId;
    if (offsetLogId === undefined) {
        const logItems = selectLogItemsByUid(getState(), userId);
        effectiveOffset = logItems.length > 0 ? logItems[logItems.length - 1].id : undefined;
    }

    return (
        await api.getModerationLogItems({
            user_id: userId,
            limit: MOD_LOG_LIMIT_PER_FETCH,
            offset: effectiveOffset === null ? undefined : effectiveOffset,
        })
    ).data;
});

interface HideReportsParams {
    entity_id: string;
    entity_type: 'user' | 'photo' | 'comment' | 'post' | 'group';
    isSingleUserModPage?: boolean;
    // Required when approving/deleting a non-user entity (e.g., users post)
    user_id?: string;
}

export const hideReports = createAction<HideReportsParams>('set/reports-updated');

export const setReportsTotal = createAction<number | undefined>('set/reports-total');
export const syncModLogItem = createAction<ModActionLogEntity>('sync/mod-log-item');
export const resetReportsList = createAction('reset/reports-list');
export const setReportsFilter = createAction<ModReportFilters>('set/reports-filter');
export const resetReportsFilter = createAction('reset/reports-filter');
export const clearModRecentContent = createAction('clear/mod-recent-content');

export interface ModContentUpdatedParams {
    entity_type: 'user' | 'photo' | 'comment' | 'post' | 'group';
    entity: AccountEntity | PostEntity | CommentEntity | GroupEntity | PhotoEntity;
}

export const removeEntityFromRecentContent = createAction<ModContentUpdatedParams>(
    'update/remove-entity-from-recent-content',
);
